import React, { FC, forwardRef, useState } from 'react';
import { SelectProps } from '@business/interfaces/select/selectProps';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { Input } from '../Input';

export const Select: FC<SelectProps> = forwardRef<
  HTMLSelectElement,
  SelectProps
>(
  (
    {
      inputWrapperClassName = '',
      label,
      labelClassName = 'pl-2 absolute text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0  peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6',
      color = 'success',
      error,
      startDecorator,
      endDecorator,
      options,
      selectClassName,
      optionDecorator,
      showAddNew,
      onAddNewDone,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
    const [newValue, setNewValue] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);

    return (
      <>
        <div
          data-testid="input-select-wrapper"
          className={`${inputWrapperClassName} ${error ? 'error' : color}`}>
          {startDecorator}
          <div className="w-full">
            {!showAddNew ? (
              <select
                {...props}
                ref={ref}
                id={props.name}
                value={props.value}
                disabled={props.disabled}
                className={selectClassName}
                data-testid={`select-input-${props.name}`}>
                {options?.map(option => (
                  <option key={option.value} value={option.value}>
                    {optionDecorator} {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="relative">
                <div
                  className={`${selectClassName} cursor-pointer`}
                  onClick={() => setIsOpen(!isOpen)}>
                  {options?.find(o => o.value === props.value)?.label ||
                    'Select'}
                </div>

                {isOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-md max-h-64 overflow-auto">
                    {/* ADD NEW SECTION */}
                    {!isAddingNew ? (
                      <div
                        className="flex items-center gap-2 px-3 py-2 text-[#49712B] text-sm font-medium cursor-pointer border-t"
                        onClick={() => setIsAddingNew(true)}>
                        <span className="text-lg font-bold">+</span>{' '}
                        {t('addNew')}
                      </div>
                    ) : (
                      <div className="p-3 space-y-2 border-t">
                        <Input
                          name="addNewValue"
                          placeholder="Enter value"
                          isWhiteTheme
                          type="text"
                          value={newValue}
                          onChange={e => setNewValue(e.target.value)}
                        />

                        <div className="flex justify-end gap-2">
                          <Button
                            btnStyle="link"
                            className="px-2 py-1 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
                            onClick={() => {
                              setIsAddingNew(false);
                              setNewValue('');
                            }}>
                            {t('cancel')}
                          </Button>

                          <Button
                            btnStyle="primary"
                            type="button"
                            className="rounded-lg px-2 py-1 font-semibold text-sm"
                            onClick={() => {
                              onAddNewDone?.(newValue);
                              setIsAddingNew(false);
                              setNewValue('');
                            }}>
                            {t('add')}
                          </Button>
                        </div>
                      </div>
                    )}
                    {/* OPTIONS */}
                    {options?.map(opt => (
                      <div
                        key={opt.value}
                        className="px-3 py-2 hover:bg-gray-100 text-black cursor-pointer text-sm"
                        onClick={() => {
                          props.onChange?.({
                            target: { name: props.name, value: opt.value }
                          } as any);
                          setIsAddingNew(false);
                          setNewValue('');
                          setIsOpen(false);
                        }}>
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {label && (
              <label
                htmlFor={props.name}
                data-testid="input-label"
                className={labelClassName}>
                {label}
              </label>
            )}
          </div>
          {endDecorator}
        </div>

        {showAddNew && isAddingNew && (
          <div className="mt-3 space-y-3">
            <Input
              name="addNewValue"
              placeholder="Enter value"
              isWhiteTheme
              type="text"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
            />

            <div className="flex gap-3">
              <Button
                btnStyle="link"
                className="px-6 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
                onClick={() => {
                  setIsAddingNew(false);
                  setNewValue('');
                }}>
                {t('cancel')}
              </Button>

              <Button
                btnStyle="primary"
                type="button"
                className="rounded-lg w-40 py-3 font-semibold text-sm"
                onClick={() => {
                  onAddNewDone?.(newValue);
                  setIsAddingNew(false);
                  setNewValue('');
                }}>
                {t('add')}
              </Button>
            </div>
          </div>
        )}

        {error && <div className="error-msg text-sm pt-1">{error}</div>}
      </>
    );
  }
);
Select.displayName = 'Select';
